import React, { useState, useEffect, useContext } from 'react';
import GameSummary from './GameSummary';
import { fetchGames, fetchGame } from '../util/api/game';
import axios from 'axios';
import { Link } from 'react-router-dom';
import chessslogo from '../images/chessslogo.png';
import { doc, getDoc, collection, getDocs, query, where, updateDoc } from "firebase/firestore";
import { firestore } from '../firebase';
import { connect } from 'react-redux';
import { ChessSocialContext } from '../util/ChessSocialContext';
import { isProfane, cleanProfanity } from '../util/filterProfanity';

// Wrapper component to handle notifications with missing games
const NotificationWithGame = ({ gameId, children, hasUnreadNotifications, navigateToGame, isFollowsTab }) => {
  const [gameExists, setGameExists] = useState(true);
  const [gameLoading, setGameLoading] = useState(true);
  
  // Check if game exists by fetching it
  useEffect(() => {
    const checkGame = async () => {
      try {
        const gameData = await fetchGame(gameId);
        if (gameData && gameData.error && gameData.isNotFound) {
          setGameExists(false);
        } else if (gameData && !gameData.error) {
          setGameExists(true);
        }
      } catch (error) {
        console.error('Error checking game:', error);
        setGameExists(false);
      } finally {
        setGameLoading(false);
      }
    };
    
    checkGame();
  }, [gameId]);
  
  // If game doesn't exist, don't render anything
  if (!gameExists) {
    return null;
  }
  
  // If still loading, show a placeholder
  if (gameLoading) {
    return <div>Loading...</div>;
  }
  
  // Create GameSummary element for display
  const gameSummaryElement = (
    <GameSummary 
      id={gameId} 
      boardWidth={275}
    />
  );
  
  return (
    <div
      key={gameId}
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        width: '100%',
        maxWidth: '1000px',
        margin: '0 auto 20px auto',
        border: hasUnreadNotifications ? '1px solid #58b047' : 'none',
        borderRadius: '8px',
        padding: hasUnreadNotifications ? '10px' : '0',
        backgroundColor: hasUnreadNotifications ? 'rgba(88, 176, 71, 0.05)' : 'transparent'
      }}
    >
      {!isFollowsTab && (
        <div style={{ marginRight: '15px', flexShrink: 0, cursor: 'pointer' }} onClick={() => navigateToGame(gameId)}>
          {gameSummaryElement}
        </div>
      )}
      {children}
    </div>
  );
};

const NotificationsTab = (props) => {
  const [activeTab, setActiveTab] = useState('comments');
  const [userGames, setUserGames] = useState([]);
  const [notifications, setNotifications] = useState(() => {
    // Try to get cached notifications from localStorage
    const cachedNotifications = localStorage.getItem('userNotifications');
    return cachedNotifications ? JSON.parse(cachedNotifications) : {
      comments: {},
      likes: {},
      follows: { followers: [] }
    };
  });
  const [loading, setLoading] = useState(false); // Changed to false initially since we're showing cached data first
  const [fetchingNew, setFetchingNew] = useState(false); // State to track background fetching
  const [lastFetchTime, setLastFetchTime] = useState(() => {
    // Get the last fetch time from localStorage or default to 0
    return parseInt(localStorage.getItem('lastNotificationsFetchTime') || '0');
  });
  const [error, setError] = useState(null);
  const [newNotifications, setNewNotifications] = useState({}); // Track which notifications are new
  const [initialDataLoaded, setInitialDataLoaded] = useState(false); // Track if initial data is loaded
  const authenticatedUserHandle = props?.user?.credentials?.handle; // Current logged-in user's handle
  const [newFollowers, setNewFollowers] = useState([]);
  const [newComments, setNewComments] = useState([]);
  const { isSidebarCollapsed } = useContext(ChessSocialContext);


  useEffect(() => {
    const fetchNotifications = async () => {
      
      
      try {
        // Fetch follower notifications
        const followersQuery = query(
          collection(firestore, "notifications"), 
          where("followedTo", "==", authenticatedUserHandle)
        );
        
        const followersData = [];
        const followersSnapshot = await getDocs(followersQuery);
        followersSnapshot.forEach((doc) => {
          
          const followerData = doc.data();
          if (!followerData.status) {
            followerData.status = 'unread';
          }
          followersData.push({
            id: doc.id,
            ...followerData
          });
        });
        
        // Sort followers by creation time (newest first)
        followersData.sort((a, b) => {
          const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
          const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
          return timeB - timeA; // Reverse chronological order
        });
        
        setNewFollowers(followersData);
        
        // Fetch comment notifications
        const commentsQuery = query(
          collection(firestore, "notifications"), 
          where("commentTo", "==", authenticatedUserHandle),
          where("type", "==", "new_comment")
        );
        
        const commentsData = [];
        const commentsSnapshot = await getDocs(commentsQuery);
        commentsSnapshot.forEach((doc) => {
          
          const commentData = doc.data();
          if (!commentData.status) {
            commentData.status = 'unread';
          }
          commentsData.push({
            id: doc.id,
            ...commentData
          });
        });
        
        // Sort comments by creation time (newest first)
        commentsData.sort((a, b) => {
          const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
          const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
          return timeB - timeA; // Reverse chronological order
        });
        
        setNewComments(commentsData);
        
        // Fetch like notifications
        const likesQuery = query(
          collection(firestore, "notifications"), 
          where("likedTo", "==", authenticatedUserHandle),
          where("type", "==", "new_like")
        );
        
        const likesData = [];
        const likesSnapshot = await getDocs(likesQuery);
        likesSnapshot.forEach((doc) => {
          
          const likeData = doc.data();
          if (!likeData.status) {
            likeData.status = 'unread';
          }
          likesData.push({
            id: doc.id,
            ...likeData
          });
        });
        
        // Sort likes by creation time (newest first)
        likesData.sort((a, b) => {
          const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
          const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
          return timeB - timeA; // Reverse chronological order
        });
        
        
        // Group likes by gameId
        const likesByGameId = likesData.reduce((groups, like) => {
          const gameId = like.gameId;
          if (!groups[gameId]) {
            groups[gameId] = [];
          }
          groups[gameId].push(like);
          return groups;
        }, {});
        
        // Add the likes data to newComments state grouped by gameId
        const updatedComments = [...commentsData];
        
        // If we have likes but no comments for a game, we need to create a blank entry
        Object.keys(likesByGameId).forEach(gameId => {
          // Check if this gameId already exists in commentsData
          const gameExists = commentsData.some(comment => comment.gameId === gameId);
          if (!gameExists) {
            // Add a placeholder comment to ensure the game card is rendered
            updatedComments.push({
              id: `placeholder-${gameId}`,
              gameId: gameId,
              isPlaceholder: true, // Flag to identify this as not a real comment
              status: 'read'
            });
          }
        });
        
        setNewComments(updatedComments);
        // Store the likes by gameId for rendering in the yellow likes container
        setNotifications(prev => ({
          ...prev,
          likesByGameId: likesByGameId
        }));
      }
      catch (err) {
        console.error("Error fetching notifications:", err);
      }
    };
    
    if (authenticatedUserHandle) {
      fetchNotifications();
    }
  }, [authenticatedUserHandle]);

  // Function to mark followers as read in Firestore
  const markFollowersAsReadInFirestore = async (followers) => {
    try {
      // Filter only unread followers that need updating
      const unreadFollowers = followers.filter(follower => follower.status === 'unread');
      
      if (unreadFollowers.length === 0) return;
      
      
      
      // Update each follower document in Firestore
      const updatePromises = unreadFollowers.map(follower => {
        const docRef = doc(firestore, "notifications", follower.id);
        return updateDoc(docRef, {
          status: 'read'
        });
      });
      
      // Wait for all updates to complete
      await Promise.all(updatePromises);
      
    } catch (err) {
      console.error("Error updating follower status in Firestore:", err);
    }
  };

  // Function to mark comments as read in Firestore
  const markCommentsAsReadInFirestore = async (comments) => {
    try {
      // Filter only unread comments that need updating
      const unreadComments = comments.filter(comment => comment.status === 'unread');
      
      if (unreadComments.length === 0) return;
      
      
      
      // Update each comment document in Firestore
      const updatePromises = unreadComments.map(comment => {
        const docRef = doc(firestore, "notifications", comment.id);
        return updateDoc(docRef, {
          status: 'read'
        });
      });
      
      // Wait for all updates to complete
      await Promise.all(updatePromises);
      
    } catch (err) {
      console.error("Error updating comment status in Firestore:", err);
    }
  };

  // Function to mark like notifications as read in Firestore
  const markLikesAsReadInFirestore = async () => {
    try {
      if (!notifications.likesByGameId) return;
      
      // Get all likes from all games and filter for unread ones
      const allLikes = Object.values(notifications.likesByGameId)
        .flat()
        .filter(like => like.status === 'unread');
      
      if (allLikes.length === 0) return;
      
      
      
      // Update each like document in Firestore
      const updatePromises = allLikes.map(like => {
        const docRef = doc(firestore, "notifications", like.id);
        return updateDoc(docRef, {
          status: 'read'
        });
      });
      
      // Wait for all updates to complete
      await Promise.all(updatePromises);
      
      // Update local state to mark likes as read
      const updatedLikesByGameId = { ...notifications.likesByGameId };
      Object.keys(updatedLikesByGameId).forEach(gameId => {
        updatedLikesByGameId[gameId] = updatedLikesByGameId[gameId].map(like => ({
          ...like,
          status: 'read'
        }));
      });
      
      // Update notifications state with read likes
      setNotifications(prev => ({
        ...prev,
        likesByGameId: updatedLikesByGameId
      }));
      
      
    } catch (err) {
      console.error("Error updating like status in Firestore:", err);
    }
  };

  // Handle page visibility changes and navigation events
  useEffect(() => {
    // Function to handle page visibility change
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        if (activeTab === 'follows') {
          // User is leaving the page while on follows tab
          markFollowersAsReadInFirestore(newFollowers);
        } else if (activeTab === 'comments') {
          // User is leaving the page while on comments tab
          markCommentsAsReadInFirestore(newComments);
          markLikesAsReadInFirestore(); // Also mark likes as read
        }
      }
    };
    
    // Function to handle before unload (page refresh/close)
    const handleBeforeUnload = () => {
      if (activeTab === 'follows') {
        // Synchronous version for before unload
        const unreadFollowers = newFollowers.filter(follower => follower.status === 'unread');
        if (unreadFollowers.length > 0) {
          markFollowersAsReadInFirestore(newFollowers);
        }
      } else if (activeTab === 'comments') {
        const unreadComments = newComments.filter(comment => comment.status === 'unread');
        if (unreadComments.length > 0) {
          markCommentsAsReadInFirestore(newComments);
          markLikesAsReadInFirestore(); // Also mark likes as read
        }
      }
    };
    
    // Add event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    // Clean up
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      
      // Also mark as read when unmounting component
      if (activeTab === 'follows') {
        markFollowersAsReadInFirestore(newFollowers);
      } else if (activeTab === 'comments') {
        markCommentsAsReadInFirestore(newComments);
        markLikesAsReadInFirestore(); // Also mark likes as read
      }
    };
  }, [activeTab, newFollowers, newComments, notifications.likesByGameId]);

  const handleTabClick = (tab) => {
    // If we're leaving the follows tab, mark all followers as read
    if (activeTab === 'follows' && tab !== 'follows') {
      // Update status to 'read' for all followers
      const updatedFollowers = newFollowers.map(follower => ({
        ...follower,
        status: 'read'
      }));
      setNewFollowers(updatedFollowers);
      
      // Update status in Firestore
      markFollowersAsReadInFirestore(newFollowers);
    }
    
    // If we're leaving the comments tab, mark all comments as read
    if (activeTab === 'comments' && tab !== 'comments') {
      // Update status to 'read' for all comments
      const updatedComments = newComments.map(comment => ({
        ...comment,
        status: 'read'
      }));
      setNewComments(updatedComments);
      
      // Update status in Firestore
      markCommentsAsReadInFirestore(newComments);
      markLikesAsReadInFirestore(); // Also mark likes as read
    }
    
    // If entering the comments tab, mark likes as read (since they'll be viewed)
    if (tab === 'comments' && activeTab !== 'comments') {
      // We'll mark likes as read since they're visible in the comments tab
      markLikesAsReadInFirestore();
    }
    
    setActiveTab(tab);

    // Mark all followers as seen when visiting the follows tab
    if (tab === 'follows' && newNotifications.follows && newNotifications.follows.followers) {
      // Create a copy of the notifications and remove the new status from follows
      const updatedNewNotifications = {
        ...newNotifications,
        follows: { followers: [] }
      };
      setNewNotifications(updatedNewNotifications);
    }
  };

  // Check if a notification is new
  const isNewNotification = (gameId, notification, type) => {
    if (!newNotifications[type] || !newNotifications[type][gameId]) {
      return false;
    }

    if (type === 'comments') {
      return newNotifications[type][gameId].some(n =>
        (n.username || n.user) === (notification.username || notification.user) &&
        n.content === notification.content
      );
    } else if (type === 'likes' || type === 'follows') {
      return newNotifications[type][gameId].includes(notification);
    }

    return false;
  };

  // Check if there are any new follows
  const hasNewFollowers = () => {
    return newFollowers.some(follower => follower.status === 'unread');
  };

  // Function to navigate to a game in a new tab
  const navigateToGame = (gameId) => {
    window.open(`/games/${gameId}`, '_blank');
  };

  // Render notifications grouped by game ID
  const renderNotificationsByGame = (notificationsObj, likesObj, isFollowsTab = false) => {
    if (loading) {
      return <div style={{ color: 'white', padding: '20px' }}>Loading notifications...</div>;
    }

    if (error) {
      return <div style={{ color: 'white', padding: '20px' }}>Error: {error}</div>;
    }

    // Only show "no games" message if we've confirmed there are no games after loading
    if (initialDataLoaded && userGames.length === 0 && !isFollowsTab) {
      return (
        <div style={{
          color: 'white',
          padding: '30px',
          textAlign: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          borderRadius: '8px',
          maxWidth: '600px',
          margin: '0 auto'
        }}>
          <h3 style={{ color: 'gold', marginBottom: '15px', fontSize: '24px' }}>No Games Found</h3>
          <p style={{ marginBottom: '20px', fontSize: '18px', lineHeight: '1.5' }}>
            You haven't created any games yet. Create a game to receive comments and likes.
          </p>
          <Link to="/Analysis">
            <button style={{
              padding: '12px 24px',
              backgroundColor: '#58b047',
              color: 'white',
              border: '2px solid gold',
              borderRadius: '5px',
              cursor: 'pointer',
              fontSize: '18px',
              fontWeight: 'bold'
            }}>
              Create Your First Game
            </button>
          </Link>
        </div>
      );
    }

    // Regular notifications (comments/likes) - unchanged existing code
    if (initialDataLoaded && Object.keys(notificationsObj).length === 0) {
      if (userGames.length > 0) {
        return <div style={{ color: 'white', padding: '20px', textAlign: 'center' }}>No notifications yet for your games. Check back later!</div>;
      } else {
        return <div style={{ color: 'white', padding: '20px', textAlign: 'center' }}>No notifications yet. Check back later!</div>;
      }
    }

    

    // Process all games to identify new notifications and sort them
    const processedGames = Object.entries(notificationsObj).map(([gameId, notifications]) => {
      // For each game, separate new and old notifications
      const newNotificationsForGame = [];
      const oldNotificationsForGame = [];

      notifications.forEach(notification => {
        const isNew = isFollowsTab
          ? isNewNotification(gameId, notification, 'follows')
          : isNewNotification(gameId, notification, 'comments');

        if (isNew) {
          newNotificationsForGame.push({ ...notification, isNew: true });
        } else {
          oldNotificationsForGame.push({ ...notification, isNew: false });
        }
      });

      // Sort each group by timestamp (newest first) before combining
      const sortByTimestamp = (a, b) => {
        const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
        const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
        return timeB - timeA; // Reverse chronological order
      };
      
      newNotificationsForGame.sort(sortByTimestamp);
      oldNotificationsForGame.sort(sortByTimestamp);

      // Create a combined array with new notifications first
      const sortedNotifications = [...newNotificationsForGame, ...oldNotificationsForGame];

      // Check if this game has new likes
      const hasNewLikes = !isFollowsTab &&
        likesObj[gameId] &&
        newNotifications.likes &&
        newNotifications.likes[gameId];

      // Get likes for this game
      const gameLikes = likesObj[gameId] || [];

      // Debug log
      

      // Find the newest notification timestamp for sorting games
      const newestTimestamp = sortedNotifications.length > 0 
        ? (sortedNotifications[0].timestamp ? new Date(sortedNotifications[0].timestamp).getTime() : 0)
        : 0;

      // If there are likes for this game, consider their timestamps too
      let newestLikeTimestamp = 0;
      if (notifications.likesByGameId && notifications.likesByGameId[gameId]) {
        const likes = notifications.likesByGameId[gameId];
        if (likes.length > 0) {
          // Find the newest like
          const newestLike = [...likes].sort((a, b) => {
            const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
            const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
            return timeB - timeA;
          })[0];
          
          newestLikeTimestamp = newestLike.timestamp ? new Date(newestLike.timestamp).getTime() : 0;
        }
      }

      // Use the newest timestamp between comments and likes
      const gameNewestTimestamp = Math.max(newestTimestamp, newestLikeTimestamp);

      return {
        gameId,
        notifications: sortedNotifications,
        hasNewLikes,
        likes: gameLikes,
        newestTimestamp: gameNewestTimestamp, // Add timestamp for sorting
        hasNew: sortedNotifications.some(n => n.isNew) || hasNewLikes
      };
    });

    // Sort games by newest notifications first, then by "has new" status
    processedGames.sort((a, b) => {
      // First sort by new status
      if (a.hasNew && !b.hasNew) return -1;
      if (!a.hasNew && b.hasNew) return 1;
      
      // If both have the same "new" status, sort by timestamp
      return b.newestTimestamp - a.newestTimestamp;
    });

    return processedGames.map(({ gameId, notifications, hasNewLikes, likes, newestTimestamp }) => {
      // For testing - ensure we have some mock likes if none exist
      const displayLikes = likes.length > 0 ? likes : ['User1', 'User2', 'User3'];
      const displayHasNewLikes = hasNewLikes || true; // Changed to false to not force green

      // For testing - simulate some new likes (in real code, this would come from newNotifications)
      const newLikes = newNotifications.likes && newNotifications.likes[gameId]
        ? newNotifications.likes[gameId]
        : displayHasNewLikes ? [displayLikes[0]] : []; // Just mark the first user as new for testing

      return (
        <div
          key={gameId}
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            width: '100%',
            maxWidth: '1000px',
            margin: '0 auto 20px auto',
            border: notifications.some(n => n.isNew) || hasNewLikes ? '1px solid #58b047' : 'none',
            borderRadius: '8px',
            padding: notifications.some(n => n.isNew) || hasNewLikes ? '10px' : '0',
            backgroundColor: notifications.some(n => n.isNew) || hasNewLikes ? 'rgba(88, 176, 71, 0.05)' : 'transparent'
          }}
        >
          {!isFollowsTab && (
            <div style={{ marginRight: '15px', flexShrink: 0, cursor: 'pointer' }} onClick={() => navigateToGame(gameId)}>
              <GameSummary 
                id={gameId} 
                boardWidth={275}
              />
            </div>
          )}

          <div style={{
            flex: 1,
            minWidth: 0, // Ensures text truncation works
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '400px', // Set max height for the notifications container
            overflow: 'hidden' // Hide overflow
          }}>

            {/* Always render likes at the top - outside the scrollable area */}
            {!isFollowsTab && (
              <div style={{
                margin: '8px 0',
                padding: '15px',
                backgroundColor: 'rgba(255, 215, 0, 0.1)', // Light gold background
                borderRadius: '4px',
                lineHeight: '1.4',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                color: '#ffd700', // Always gold for the container
                display: 'block', // Always display the likes container
                flexShrink: 0, // Prevent this from shrinking
                borderLeft: displayHasNewLikes ? '3px solid #58b047' : '3px solid #ffd700' // Add left border
              }}>
                {notifications.likesByGameId && notifications.likesByGameId[gameId] && notifications.likesByGameId[gameId].length > 0 ? (
                  <span>
                    {/* Sort likes by timestamp (newest first) before displaying */}
                    {[...notifications.likesByGameId[gameId]]
                      .sort((a, b) => {
                        const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
                        const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
                        return timeB - timeA; // Newest first
                      })
                      .map((like, i) => {
                        const isNewLike = like.status === 'unread';
                        const isLast = i === notifications.likesByGameId[gameId].length - 1;
                        const isSecondToLast = i === notifications.likesByGameId[gameId].length - 2;
                        
                        return (
                          <React.Fragment key={i}>
                            <span style={{ color: isNewLike ? '#58b047' : '#ffd700' }}>
                              {like.likedBy}
                            </span>
                            {isSecondToLast ? ' and ' : !isLast ? ', ' : ''}
                          </React.Fragment>
                        );
                      })}
                    <span style={{ color: '#ffd700' }}> liked your post.</span>
                  </span>
                ) : (
                  <span>This post has no likes yet.</span>
                )}
              </div>
            )}

            {/* Scrollable container for comments */}
            <div style={{
              overflowY: 'auto', // Enable vertical scrolling
              flex: 1, // Take remaining space
              paddingRight: '5px' // Add some padding for the scrollbar
            }}>
              {notifications.map((notification, index) => (
                <div
                  key={index}
                  style={{
                    margin: '8px 0',
                    padding: '15px', // Add padding for follows tab
                    textAlign: isFollowsTab ? 'center' : 'left',
                    backgroundColor: notification.isNew ? 'rgba(88, 176, 71, 0.1)' : 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '4px',
                    lineHeight: '1.4',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    color: notification.isNew ? '#58b047' : 'white', // Green if new, white otherwise
                    borderLeft: notification.isNew ? '3px solid #58b047' : 'none'
                  }}
                >
                  {isFollowsTab
                    ? `${notification.handle || notification} started following you.`
                    : `${notification.username || notification.user}: "${cleanProfanity(notification.content)}"`}
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    });
  };

  return (
    <div className="app-container" style={{ background: "", color: "#fff" }}>
      <div className="main-column explore-container" style={{
        maxHeight: "calc(100vh - 50px)", // Adjust height as needed
        overflowY: "auto", // Enable vertical scrolling
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "20px",
        background: 'linear-gradient(to bottom, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
        maxWidth: isSidebarCollapsed ? 'calc(100vw - 125px)' : 'calc(100vw - 250px)',
        width: '100%',

      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          borderBottom: '4px solid black',

          width: '100%', // Ensure full width
          position: 'relative' // For positioning the loading indicator
        }}>
          {/* Loading indicator - only shown during fetching */}
          {fetchingNew && (
            <div
              style={{
                position: 'absolute',
                right: '50px',
                top: '10px',

                zIndex: 2
              }}
            >
              <img
                src={chessslogo}
                alt="Refreshing data"
                style={{
                  width: '75px',
                  height: 'auto',
                  animation: 'spin 2s linear infinite'
                }}
              />
              <style>
                {`
                  @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                `}
              </style>
            </div>
          )}

          <button
            style={{
              flex: 1,
              padding: '5px',
              paddingBottom: '50px',
              color: activeTab === 'comments' ? '#ffd700' : 'white',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              borderBottom: activeTab === 'comments' ? '4px solid #ffd700' : '2px solid white',
              fontSize: '30px',
              textAlign: 'center', // Center text,
              marginTop: '25px',
            }}
            onClick={() => handleTabClick('comments')}
          >
            Post Notifications
          </button>
          <button
            style={{
              flex: 1,
              padding: '5px',
              paddingBottom: '50px',
              color: activeTab === 'follows' ? '#ffd700' : hasNewFollowers() ? '#58b047' : 'white',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              borderBottom: activeTab === 'follows' ? '4px solid #ffd700' : hasNewFollowers() ? '2px solid #58b047' : '2px solid white',
              fontSize: '30px',
              textAlign: 'center', // Center text
              marginTop: '25px',
            }}
            onClick={() => handleTabClick('follows')}
          >
            <div style={{ position: 'relative', display: 'inline-block' }}>
              New Followers
              {hasNewFollowers() && (
                <div
                  style={{
                    position: 'absolute',
                    top: '-2px',
                    right: '-12px',
                    width: '8px',
                    height: '8px',
                    backgroundColor: '#58b047',
                    borderRadius: '50%',
                    border: '1px solid #58b047',
                    zIndex: 10
                  }}
                ></div>
              )}
            </div>
          </button>
        </div>

        <div style={{
          padding: '20px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px',
          width: '80%',
        }}>
          {activeTab === 'comments' && 
            <div style={{ width: '100%' }}>
             
              
              {/* Comment Notifications Cards - Grouped by gameId */}
              {newComments.length > 0 ? (
                (() => {
                  // Group comments by gameId
                  const commentsByGameId = newComments.reduce((groups, comment) => {
                    const gameId = comment.gameId;
                    if (!groups[gameId]) {
                      groups[gameId] = [];
                    }
                    groups[gameId].push(comment);
                    return groups;
                  }, {});
                  
                  // Sort comments within each game by timestamp (newest first) only
                  // Preserve the exact timestamp ordering
                  Object.keys(commentsByGameId).forEach(gameId => {
                    commentsByGameId[gameId].sort((a, b) => {
                      const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
                      const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
                      return timeB - timeA; // Newest first
                    });
                  });
                  
                  // Convert the grouped comments object into an array of game entries
                  const gameEntries = Object.entries(commentsByGameId);
                  
                  // Prepare game entries with additional metadata for sorting
                  const enhancedGameEntries = gameEntries.map(([gameId, comments]) => {
                    // Check if any comment in this group is unread
                    const hasUnreadComments = comments.some(comment => comment.status === 'unread');
                    
                    // Check if any likes for this game are unread
                    const hasUnreadLikes = notifications.likesByGameId && 
                                         notifications.likesByGameId[gameId] && 
                                         notifications.likesByGameId[gameId].some(like => like.status === 'unread');
                    
                    // Find the newest timestamp across all comments for this game
                    const newestCommentTimestamp = comments.length > 0 ? 
                      (comments[0].timestamp ? new Date(comments[0].timestamp).getTime() : 0) : 0;
                    
                    // Find the newest timestamp across all likes for this game
                    let newestLikeTimestamp = 0;
                    if (notifications.likesByGameId && notifications.likesByGameId[gameId]) {
                      const likes = notifications.likesByGameId[gameId];
                      if (likes.length > 0) {
                        // Sort likes by timestamp to get the newest one
                        const sortedLikes = [...likes].sort((a, b) => {
                          const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
                          const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
                          return timeB - timeA; // Newest first
                        });
                        newestLikeTimestamp = sortedLikes[0].timestamp ? 
                          new Date(sortedLikes[0].timestamp).getTime() : 0;
                      }
                    }
                    
                    // Use the most recent timestamp between comments and likes
                    const newestTimestamp = Math.max(newestCommentTimestamp, newestLikeTimestamp);
                    
                    // Flag if this game has any unread notifications (either comments or likes)
                    const hasUnreadNotifications = hasUnreadComments || hasUnreadLikes;
                    
                    return {
                      gameId,
                      comments,
                      hasUnreadNotifications,
                      newestTimestamp
                    };
                  });
                  
                  // Sort games: first by unread status, then by timestamp
                  enhancedGameEntries.sort((a, b) => {
                    // First prioritize unread notifications
                    if (a.hasUnreadNotifications && !b.hasUnreadNotifications) return -1;
                    if (!a.hasUnreadNotifications && b.hasUnreadNotifications) return 1;
                    
                    // Then sort by timestamp (newest first)
                    return b.newestTimestamp - a.newestTimestamp;
                  });
                  
                  // Render the sorted game entries
                  return enhancedGameEntries.map(({ gameId, comments, hasUnreadNotifications }) => {
                    // Check if any comment in this group is unread (for styling)
                    const hasUnreadComments = comments.some(comment => comment.status === 'unread');
                    
                    return (
                      <NotificationWithGame
                        key={gameId}
                        gameId={gameId}
                        hasUnreadNotifications={hasUnreadNotifications}
                        navigateToGame={navigateToGame}
                      >
                        <div style={{
                          flex: 1,
                          minWidth: 0, // Ensures text truncation works
                          display: 'flex',
                          flexDirection: 'column',
                          maxHeight: '450px', // Set max height for the notifications container
                          overflow: 'hidden' // Hide overflow
                        }}>
                          {/* Simplified likes section */}
                          <div style={{
                            margin: '8px 0',
                            padding: '15px',
                            backgroundColor: 'rgba(255, 215, 0, 0.1)', // Light gold background
                            borderRadius: '4px',
                            lineHeight: '1.4',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            color: '#ffd700', // Always gold for the container
                            display: 'block', // Always display the likes container
                            flexShrink: 0, // Prevent this from shrinking
                            borderLeft: notifications.likesByGameId && notifications.likesByGameId[gameId] && 
                                       notifications.likesByGameId[gameId].some(like => like.status === 'unread') 
                                       ? '3px solid #58b047' : '3px solid #ffd700' // Green if any unread likes
                          }}>
                            {notifications.likesByGameId && notifications.likesByGameId[gameId] && notifications.likesByGameId[gameId].length > 0 ? (
                              <span>
                                {/* Sort likes by timestamp (newest first) ONLY - don't prioritize unread status */}
                                {[...notifications.likesByGameId[gameId]]
                                  .sort((a, b) => {
                                    const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
                                    const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
                                    return timeB - timeA; // Newest first
                                  })
                                  .map((like, i) => {
                                    const isNewLike = like.status === 'unread';
                                    const isLast = i === notifications.likesByGameId[gameId].length - 1;
                                    const isSecondToLast = i === notifications.likesByGameId[gameId].length - 2;
                                    
                                    return (
                                      <React.Fragment key={i}>
                                        <span style={{ color: isNewLike ? '#58b047' : '#ffd700' }}>
                                          {like.likedBy}
                                        </span>
                                        {isSecondToLast ? ' and ' : !isLast ? ', ' : ''}
                                      </React.Fragment>
                                    );
                                  })}
                                <span style={{ color: '#ffd700' }}> liked your post.</span>
                              </span>
                            ) : (
                              <span>This post has no likes yet.</span>
                            )}
                          </div>

                          {/* Comment content - multiple comments for same gameId */}
                          <div style={{
                            overflowY: 'auto', // Enable vertical scrolling
                            flex: 1, // Take remaining space
                            paddingRight: '5px' // Add some padding for the scrollbar
                          }}>
                            {/* Sort the comments strictly by timestamp (newest first) */}
                            {comments
                              .sort((a, b) => {
                                // Sort purely by timestamp (newest first)
                                const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
                                const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
                                return timeB - timeA;
                              })
                              .map((comment, index) => (
                                <div
                                  key={index}
                                  style={{
                                    margin: '8px 0',
                                    padding: '15px',
                                    textAlign: 'left',
                                    backgroundColor: comment.status === 'unread' ? 'rgba(88, 176, 71, 0.1)' : 'rgba(255, 255, 255, 0.05)',
                                    borderRadius: '4px',
                                    lineHeight: '1.4',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    color: comment.status === 'unread' ? '#58b047' : 'white', // Green if new, white otherwise
                                    borderLeft: comment.status === 'unread' ? '3px solid #58b047' : 'none'
                                  }}
                                >
                                  {comment.isPlaceholder ? 
                                    <span>This post has comments...</span> :
                                    `${comment.commentBy}: "${cleanProfanity(comment.commentContent)}"`}
                                </div>
                              ))}
                          </div>
                        </div>
                      </NotificationWithGame>
                    );
                  });
                })()
              ) : (
                <div style={{
                  color: 'white',
                  padding: '40px',
                  textAlign: 'center',
                  fontSize: '20px',
                  width: '100%',
                  backgroundColor: 'rgba(0, 0, 0, 0.2)',
                  borderRadius: '8px',
                  margin: '20px auto'
                }}>
                  No comment notifications yet. Check back later!
                </div>
              )}
            </div>
          }
          {activeTab === 'follows' && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              {newFollowers.length > 0 ? (
                newFollowers
                  .sort((a, b) => {
                    // First sort by read status (unread first)
                    if (a.status === 'unread' && b.status !== 'unread') return -1;
                    if (a.status !== 'unread' && b.status === 'unread') return 1;
                    
                    // Then sort by timestamp (newest first)
                    const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
                    const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
                    return timeB - timeA;
                  })
                  .map((follower, index) => {
                    const isUnread = follower.status === 'unread';
                    return (
                      <div
                        key={index}
                        style={{
                          margin: '10px 0',
                          padding: '20px',
                          backgroundColor: isUnread ? 'rgba(88, 176, 71, 0.1)' : 'rgba(30, 30, 50, 0.3)',
                          borderRadius: '8px',
                          borderLeft: isUnread ? '4px solid #58b047' : 'none',
                          color: isUnread ? '#58b047' : '#fff',
                          fontSize: '18px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
                        }}
                      >
                        <Link
                          to={`/profile/${follower.followedBy}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                            fontWeight: isUnread ? 'bold' : 'normal'
                          }}
                        >
                          {follower.followedBy} started following you
                        </Link>
                      </div>
                    );
                  })
              ) : (
                <div style={{
                  color: 'white',
                  padding: '40px',
                  textAlign: 'center',
                  fontSize: '20px',
                  width: '100%',
                  backgroundColor: 'rgba(0, 0, 0, 0.2)',
                  borderRadius: '8px',
                  margin: '20px auto'
                }}>
                  No new followers yet. Check back later!
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  data: state.data,
  user: state.user,
});

export default connect(mapStateToProps, {})(NotificationsTab);
